import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import * as customerSupportApi from '../../apis/customer-support-api';
import { GetTripPaymentResponse } from "../../components/details-view/payments-data/types";
import { ITripsPaymentsResponse } from "../../models/customer-data.interface";
import { resetErrorDialog, setErrorDialog } from "./notification-slice";
import SliceError from "../../utils/SliceError";

export type SearchType = 'PAYMENT';

export interface IPaymentsState {
  list: GetTripPaymentResponse[] | null;
  resetList: boolean;
  cursor: null | string;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  search: {
    identifier: {
      startDateRange?: string,
      stopDateRange?: string,
      state?: string,
      serviceProvider?: string,
    };
    type: SearchType;
  };
}

const initialPaymentsState: IPaymentsState = {
  list: null,
  resetList: true,
  cursor: null,
  search: {
    identifier: {
      startDateRange: "",
      stopDateRange: "",
      state: "",
      serviceProvider: ""
    },
    type: "PAYMENT"
  },
  status: "idle",
}

export interface FetchPaymentsDataParams {
  paymentState: string,
  formDate?: string,
  toDate?: string,
  serviceProvider?: string,
  cursor?: string,
  resetList?: boolean,
}

export const fetchPaymentsData = createAsyncThunk<
  ITripsPaymentsResponse,
  FetchPaymentsDataParams | undefined,
  { state: RootState }
>('payments/fetchPaymentsData', async (payload, { dispatch, rejectWithValue }) => {
  dispatch(resetErrorDialog());

  try {
    const { paymentState, cursor, formDate, serviceProvider, toDate, resetList } = payload;
    const response = await customerSupportApi.getTripPayments(paymentState, cursor, formDate, toDate, serviceProvider);

    if (response.errors && response.errors?.length > 0) {
      throw new SliceError({
        code: 'SERVICE_API_ERROR',
        title: 'Service error',
        message: 'Error while fetching trip payments'
      })
    }

    response.resetList = resetList;
    return response;
  }
  catch (error) {
    let err = error;

    if (error instanceof SliceError) {
      err = error as SliceError;
    }

    dispatch(
      setErrorDialog({
        code: err.code || err.name,
        message: err.message || "Something went wrong",
        title: err.title || err.name
      })
    );

    return rejectWithValue(err.message);
  }
},
  {
    condition: async (payload, { getState }) => {
      const { paymentState } = payload;
      if (paymentState) {
        return true
      }

      const { payments } = getState();
      return payments.status === 'idle'; // execute the fetch function only when the status is idle
    },
  }
);

export const resetAndFetchPayments = createAsyncThunk<
  ITripsPaymentsResponse,
  FetchPaymentsDataParams | undefined,
  { state: RootState }>(
    'payments/resetAndFetch',
    async (payload, { dispatch }) => {
      payload.resetList = true;
      return dispatch(fetchPaymentsData(payload)).unwrap();
    }
  );

export const paymentsSlice = createSlice({
  name: 'payments',
  initialState: initialPaymentsState,
  reducers: {
    setPaymentSearchType(state, action) {
      if (!action.payload) {
        state.search.identifier = undefined;
        state.search.type = undefined;
      } else {
        state.search.type = action.payload.type;
      }
    },
    resetPaymentsList(state) {
      state.list = null;
      state.resetList = true;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchPaymentsData.pending, (state) => {
        state.status = 'loading';

        if (state.resetList) {
          state.list = null;
        }
      })
      .addCase(fetchPaymentsData.fulfilled, (state, action) => {
        if (!action.payload?.data) {
          state.status = 'failed';
          return
        }

        state.status = 'succeeded';

        const response: ITripsPaymentsResponse = action.payload.data as unknown as ITripsPaymentsResponse;
        if (action.payload?.resetList || state.resetList) {
          state.list = [...response.data];
        } else {
          state.list = [...(state?.list || []), ...response.data];
        }

        state.resetList = false;
        state.cursor = response.cursor;
      })
      .addCase(fetchPaymentsData.rejected, (state) => {
        state.status = 'failed';
      })
  }
});

export const selectPaymentsStore = () => (state: RootState) => {
  return state.payments;
};

export const {
  setPaymentSearchType,
  resetPaymentsList,
} = paymentsSlice.actions;

export default paymentsSlice.reducer;
